import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { PlatformMockup } from '../components/ProductMockups';
import { capabilities } from '../mock';

const PlatformPage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="The Platform — Integrations, AI, Analytics"
        description="The BriefingIQ + EventIQ platform: shared integrations, AI-powered analytics, and enterprise-grade reporting. Native Salesforce, Outlook, Zoom, and Teams."
        path="/platform"
        keywords={['SaaS platform', 'Salesforce integration', 'Outlook integration', 'AI analytics', 'enterprise integrations']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Platform', path: '/platform' },
        ]}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">The Platform</div>
          <h1 className="headline-xl text-ink mb-6">
            One platform.<br />
            <span className="text-ink-3">Every integration. All the intelligence.</span>
          </h1>
          <p className="text-lg md:text-xl text-ink-2 max-w-2xl mx-auto leading-relaxed mb-9">
            BriefingIQ and EventIQ share the same platform — with the integrations, AI, and reporting your teams already trust.
          </p>
          <button onClick={onOpenDemo} className="btn-primary">
            Request a demo <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-6 mt-16">
          <PlatformMockup />
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Capabilities</div>
            <h2 className="headline-lg text-ink">Shared, by design.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line-2)] rounded-3xl overflow-hidden border border-[var(--line-2)]">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="bg-white p-8 lg:p-10">
                  <Icon className="w-6 h-6 text-ink mb-6" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-ink mb-3">{cap.title}</h3>
                  <p className="text-sm text-ink-2 leading-relaxed">{cap.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Security</div>
              <h2 className="headline-md text-ink mb-6">Enterprise-grade by default.</h2>
              <p className="text-ink-2 leading-relaxed mb-6">
                SOC 2 compliant architecture, role-based access control, audit logs, single sign-on, and data residency options that match global enterprise requirements.
              </p>
              <Link to="/contact" className="btn-link">Talk to security <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Extensibility</div>
              <h2 className="headline-md text-ink mb-6">Open APIs and webhooks.</h2>
              <p className="text-ink-2 leading-relaxed mb-6">
                Modern REST APIs, webhooks, and event streams so briefing and event data flows exactly where your business systems need it.
              </p>
              <Link to="/contact" className="btn-link">API reference <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PlatformPage;
